import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/lib/site";

const LINKS = [
  { to: "/catalogue", label: "Catalogue" },
  { to: "/services", label: "Services" },
  { to: "/areas", label: "Areas" },
  { to: "/faq", label: "FAQ" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-bone/85 backdrop-blur-md border-b border-ink/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between h-16 sm:h-20">
        <Link to="/" className="flex items-baseline gap-2" data-testid="nav-logo">
          <span className="font-display text-xl sm:text-2xl font-bold tracking-tight text-ink">Breeze Craft</span>
          <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Sydney</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-300 ${isActive ? "text-terracotta" : "text-ink-soft hover:text-ink"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href={PHONE_HREF} data-testid="nav-phone-link" className="hidden md:block font-mono text-xs tracking-wider text-ink-soft hover:text-terracotta transition-colors">
            {PHONE}
          </a>
          <Link
            to="/quote"
            data-testid="nav-get-quote-button"
            className="hidden sm:inline-flex bg-ink text-bone text-sm font-medium px-5 py-2.5 hover:bg-terracotta transition-colors duration-300"
          >
            Get a Quote
          </Link>
          <button
            data-testid="nav-mobile-menu-button"
            className="lg:hidden p-2 text-ink"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname + "-menu"}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-ink/10 bg-bone"
            data-testid="nav-mobile-menu"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="py-2.5 text-base font-medium text-ink border-b border-ink/5"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/quote"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-get-quote-button"
                className="mt-3 bg-terracotta text-white text-center font-medium px-5 py-3"
              >
                Get a Quote
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
